import { useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LandingPage from './pages/LandingPage';
import LandingPageLoginButtonTest from './pages/LandingPageLoginButtonTest';
import OnboardingPage from './pages/OnboardingPage';
import AuthPage from './pages/AuthPage';
import BrowsePage from './pages/BrowsePage';
import ProfilePage from './pages/ProfilePage';
import ProfileEditPage from './pages/ProfileEditPage';
import AdminPage from './pages/AdminPage';
import ChatPage from './pages/ChatPage';
import SettingsPage from './pages/SettingsPage';
import PremiumPage from './pages/PremiumPage';
import LegalPage from './pages/LegalPage';
import FeedbackPage from './pages/FeedbackPage';
import AuthCallback from './components/AuthCallback';
import InstallBanner from './components/InstallBanner';
import ImpersonationBanner from './components/ImpersonationBanner';
import { trackPageView } from './lib/analytics';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes({ darkMode, setDarkMode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Track SPA route changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Check URL fragment for Google OAuth session_id - process BEFORE normal routing
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Footer only on the landing page for non-logged-in visitors
  const isLandingPage = location.pathname === '/' || location.pathname === '/login-button-test';
  const showFooter = !user && isLandingPage;

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? '' : 'light-mode'}`} style={{ background: 'var(--yb-bg)' }}>
      <ImpersonationBanner />
      <Navbar darkMode={darkMode} setDarkMode={setDarkMode} />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={user ? <Navigate to="/browse" /> : <LandingPage />} />
          {/* A/B test variant — preview only, not linked from nav */}
          <Route path="/login-button-test" element={user ? <Navigate to="/browse" /> : <LandingPageLoginButtonTest />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/auth" element={user ? <Navigate to="/browse" /> : <AuthPage />} />
          <Route path="/browse" element={<ProtectedRoute><BrowsePage /></ProtectedRoute>} />
          <Route path="/profile/:userId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile/edit" element={<ProtectedRoute><ProfileEditPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/premium" element={<ProtectedRoute><PremiumPage /></ProtectedRoute>} />
          <Route path="/legal/:type" element={<LegalPage />} />
          <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      {showFooter && <Footer />}
      {!user && isLandingPage && <InstallBanner />}
    </div>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(true);

  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <AppRoutes darkMode={darkMode} setDarkMode={setDarkMode} />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
